import { motion } from "framer-motion";
import { Phone, MapPin, ArrowRight, DoorOpen, Warehouse, Cog, KeyRound, Zap, MoonStar, PhoneCall, Wrench, DoorClosed, Hammer, ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MobileDock } from "./MobileDock";
import { CallBand } from "./CallBand";
import { Reveal } from "./Reveal";
import { useSEO } from "@/hooks/useSEO";
import { SITE, TEL_HREF } from "@/siteConfig";
import { SERVICE_PAGES } from "@/servicePages";

const ICONS = { DoorOpen, Warehouse, Cog, KeyRound, Zap, MoonStar, PhoneCall, Wrench, DoorClosed, Hammer, ShieldCheck };

export const ServicePage = ({ page }) => {
  const Icon = ICONS[page.icon];
  const related = SERVICE_PAGES.filter((p) => p.slug !== page.slug).slice(0, 8);

  useSEO({
    title: page.title,
    description: page.description,
    path: `/${page.slug}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          serviceType: page.serviceType,
          name: `${page.serviceType} Roma`,
          provider: { "@id": "https://fixromah24.com/#business" },
          areaServed: "Roma e provincia",
          url: `https://fixromah24.com/${page.slug}`,
          description: page.description,
        },
        {
          "@type": "FAQPage",
          mainEntity: page.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  });

  return (
    <div
      data-testid={`service-page-${page.slug}`}
      className="min-h-screen overflow-x-clip bg-[#0A0D12] font-body text-zinc-100 antialiased"
    >
      <Header />
      <main>
        <section className="relative overflow-hidden pt-28 pb-14 sm:pt-36 sm:pb-20">
          <div className="hero-grid-bg pointer-events-none absolute inset-0" />
          <div className="pointer-events-none absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-[120px]" />

          <div className="relative mx-auto grid max-w-7xl grid-cols-12 items-center gap-y-10 px-4 sm:px-6 lg:gap-x-10 lg:px-8">
            <div className="col-span-12 min-w-0 lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-6 inline-flex max-w-full items-center gap-2.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="radar-ping absolute inline-flex h-full w-full rounded-full bg-amber-400" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
                </span>
                <span className="font-tech text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] sm:tracking-[0.25em] text-amber-300">
                  Pronto intervento H24 · Anche di notte
                </span>
              </motion.div>

              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] text-zinc-50">
                {page.h1}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
              >
                {page.sub}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-8"
              >
                <a
                  href={TEL_HREF}
                  data-testid={`service-page-${page.slug}-call-button`}
                  className="group flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-amber-400 px-9 py-4 text-base sm:text-lg font-extrabold uppercase tracking-wide text-zinc-950 transition-all hover:bg-amber-300 hover:shadow-[0_0_44px_rgba(245,158,11,0.45)] active:scale-[0.98]"
                >
                  <Phone className="h-5 w-5 transition-transform group-hover:rotate-12" />
                  Chiama ora — {SITE.phoneDisplay}
                </a>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-6 flex items-center gap-2 text-sm text-zinc-500"
              >
                <MapPin className="h-4 w-4 text-amber-400/80" />
                Roma e provincia
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="col-span-12 min-w-0 lg:col-span-5"
            >
              <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
                <img
                  src={page.image}
                  alt={page.imageAlt}
                  className="h-[260px] sm:h-[360px] w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A0D12]/80 px-4 py-3 backdrop-blur-md">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-400/25 bg-amber-400/10 text-amber-400">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-tech text-[10px] uppercase tracking-[0.25em] text-amber-400">
                    {page.serviceType}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section
          data-testid={`service-page-${page.slug}-problems`}
          className="border-y border-white/[0.06] bg-[#0D1119] py-16 sm:py-20"
        >
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">
                {page.problemsTitle}
              </h2>
              <ul className="mt-8 space-y-3.5">
                {page.problems.map((problem) => (
                  <li
                    key={problem}
                    className="flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-[#111620] px-5 py-4 text-sm sm:text-base text-zinc-200"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {problem}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-9 space-y-5">
                {page.paragraphs.map((text, idx) => (
                  <p
                    key={idx}
                    className="text-sm sm:text-base leading-relaxed text-zinc-400"
                  >
                    {text}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <CallBand
          id={`page-${page.slug}`}
          title="Hai questo problema adesso?"
          text="Descrivici il guasto al telefono e indica la tua zona: verifichiamo subito la disponibilità del servizio."
        />

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">
                Domande frequenti
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8">
              <Accordion type="single" collapsible className="w-full">
                {page.faqs.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`page-faq-${idx}`}
                    data-testid={`service-page-${page.slug}-faq-${idx + 1}`}
                    className="border-white/[0.08]"
                  >
                    <AccordionTrigger className="text-left font-display text-base sm:text-lg font-semibold text-zinc-100 hover:text-amber-300 hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base leading-relaxed text-zinc-400">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <section
          data-testid={`service-page-${page.slug}-related`}
          className="border-t border-white/[0.06] bg-[#0D1119] py-14"
        >
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-tech text-xs font-semibold uppercase tracking-[0.3em] text-amber-400">
                Altri interventi a Roma
              </h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {related.map((rel) => (
                  <li key={rel.slug}>
                    <a
                      href={`/${rel.slug}`}
                      data-testid={`related-link-${rel.slug}`}
                      className="group flex items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-[#111620] px-5 py-4 text-sm font-semibold text-zinc-200 transition-all hover:border-amber-400/40 hover:text-amber-300"
                    >
                      {rel.h1}
                      <ArrowRight className="h-4 w-4 shrink-0 text-zinc-600 transition-all group-hover:translate-x-1 group-hover:text-amber-400" />
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <MobileDock />
    </div>
  );
};
